"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  ArrowRight,
  Mic,
  Crown,
  Layers,
  Network,
  Compass,
  Target,
  Shield,
  Eye,
  Vote,
  Lock,
  Briefcase,
  Scale,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TabsBlock } from "@/lib/blocks/tabs-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function PentaPsFrameworkPage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · The Penta P's Framework"
        overlineClassName="text-cyan"
        title={
          <>
            A Closed-Loop System of <span className="text-gradient-premium">Five Interconnected Pillars</span>
          </>
        }
        subtitle="The Penta P's Framework is a closed-loop strategic sovereignty system built on five integrated pillars — Perception, Privacy, Politics, Policing, and Policy. Each pillar represents a critical domain of sovereign-scale operations. Together, they form an interdependent system where intelligence flows continuously between pillars."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "Penta P's Framework" }]}
        badge="Five pillars · Closed loop"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
            <Sparkles className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Featured Snippet Definition</span>
          </div>
          <h2 className="text-display-md mb-5">{`What the Penta P's Framework is.`}</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">{`The Penta P's Framework`}</span> is a closed-loop strategic sovereignty system built on five integrated pillars: Perception, Privacy, Politics, Policing, and Policy. Each pillar represents a critical domain of sovereign-scale operations. Together, they form an interdependent system where intelligence flows continuously between pillars — Perception informs Politics, Politics informs Policy, Policy directs Policing, Policing protects Privacy, and Privacy ensures the entire system operates without exposure.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Unlike conventional consulting frameworks that treat disciplines in isolation, the Penta P's Framework operates on a single premise: that no pillar functions effectively without the others. Perception without Privacy is exposure. Politics without Intelligence is guesswork. Policing without Policy is chaos. The framework eliminates these failures by binding each domain into a unified operational architecture.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Layers className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Five Pillars</span>
          </div>
          <h2 className="text-display-md mb-4">Core definitions of each pillar.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { icon: Eye, title: "Perception", body: "The discipline of shaping, controlling, and defending information visibility across all digital and physical channels. Reputation management, narrative engineering, search media dominance, content amplification, negative information suppression, brand protection.", accent: "red" },
            { icon: Lock, title: "Privacy", body: "The architecture of sovereign-grade information security, encryption, and communication protection. End-to-end encryption, secure communication infrastructure, data sovereignty, insider threat protection, classified network security.", accent: "purple" },
            { icon: Vote, title: "Politics", body: "The intelligence-driven discipline of electoral strategy, voter engagement, and political campaign architecture. Ground-level intelligence gathering, voter micro-segmentation, electoral timing optimization, coalition building, campaign execution.", accent: "cyan" },
            { icon: Shield, title: "Policing", body: "The domain of intelligence coordination, law enforcement support, security operations, and threat neutralization. Intelligence agency coordination, counter-terrorism frameworks, threat assessment, forensic intelligence, defense infrastructure.", accent: "emerald" },
            { icon: Scale, title: "Policy", body: "The framework for governance transformation, eGovernance implementation, and institutional delivery systems. Digital governance, institutional reform, scheme implementation tracking, citizen feedback integration.", accent: "amber-gold" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Network className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Closed-Loop System</span>
          </div>
          <h2 className="text-display-md mb-4">How the five pillars feed each other.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The Penta P's Framework is not a matrix of five independent capabilities. It is a closed-loop system where intelligence flows continuously between pillars, each reinforcing the others in a self-sustaining cycle.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { flow: "Perception → Politics", body: "Intelligence gathered through perception monitoring provides Politics with the information environment needed to target voters and time campaigns." },
            { flow: "Politics → Policy", body: "Ground-truth data from voter engagement informs Policy priorities — ensuring governance frameworks respond to actual citizen needs." },
            { flow: "Policy → Policing", body: "Governance priorities and legal frameworks direct Policing operations, ensuring security activities align with institutional mandates." },
            { flow: "Policing → Privacy", body: "Security intelligence identifies threats to communication infrastructure, enabling Privacy to deploy protections where most needed." },
            { flow: "Privacy → Perception", body: "Cryptographic certainty over communication channels allows Perception operations to proceed without fear of exposure or interception." },
          ].map((p, i) => {
            const accentColors = ["red", "purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accentColors[i]];
            return (
              <motion.div
                key={p.flow}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{p.flow}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Target className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Compound Outcomes</span>
          </div>
          <h2 className="text-display-md mb-4">What the framework produces as a whole.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { title: "Crisis Transformation", body: "Three pillars activate simultaneously: containment, threat identification, and communication security. 85-95% success rates at 384x to 1,416x speed.", accent: "red" },
            { title: "Brand Protection", body: "Reputation monitoring through Perception, secure communication through Privacy, and political positioning through Politics — operating on all fronts simultaneously.", accent: "purple" },
            { title: "Election Architecture", body: "All five pillars deploy: ground-level intelligence, messaging amplification, competitive monitoring, communication security, and governance preparation.", accent: "cyan" },
            { title: "National Security", body: "Multi-agency coordination, sovereign security architecture, digital policy implementation, and real-time threat intelligence — integrated five-pillar operations.", accent: "emerald" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Network className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", desc: "The integrated discipline the framework operationalizes.", accent: "red" },
            { term: "Digital Conglomerate", href: "/glossary/digital-conglomerate/", desc: "The organizational form that executes the framework.", accent: "purple" },
            { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", desc: "The six-stage methodology that operationalizes each pillar.", accent: "emerald" },
            { term: "Operational Excellence", href: "/glossary/operational-excellence/", desc: "The engineered system the framework is built upon.", accent: "amber-gold" },
            { term: "Perception Management", href: "/glossary/perception-management/", desc: "The Perception pillar in operational detail.", accent: "red" },
            { term: "eGovernance", href: "/glossary/egovernance/", desc: "The Policy pillar in operational detail.", accent: "lime" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Layers className="size-3.5 text-red" />
            <span className="text-overline text-red">Penta-P Tabs</span>
          </div>
          <h2 className="text-display-md mb-4">Five tabs, five pillars, one framework.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The Penta P's Framework is examined through five tabs — one for each pillar. Each tab isolates the pillar's domain, its operating principles, and its integration with the closed-loop system.
          </p>
        </div>
        <TabsBlock
          tabs={[
            {
              label: "Perception",
              value: "perception",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-red">Pillar 1 — Perception</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">The discipline of shaping, controlling, and defending information visibility across all digital and physical channels. Reputation management, narrative engineering, search media dominance, content amplification, negative information suppression, brand protection — operating as a unified architecture.</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">Perception management is the first pillar because every other pillar operates within the perceptual environment that Perception defines. The audience is king. The public decides what to view and how to interpret it. The role of Perception is to ensure that authentic truths are presented, amplified, and defended with maximum impact.</p>
                </div>
              ),
            },
            {
              label: "Privacy",
              value: "privacy",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-purple">Pillar 2 — Privacy</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">The architecture of sovereign-grade information security, encryption, and communication protection. End-to-end encryption, secure communication infrastructure, data sovereignty, insider threat protection, classified network security — the always-active security envelope that protects every other pillar.</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">Privacy is the second pillar because no other pillar can function without the cryptographic certainty that communications and data are protected. Privacy ensures the entire system operates without exposure. Privacy → Perception: cryptographic certainty allows Perception operations to proceed without fear of interception.</p>
                </div>
              ),
            },
            {
              label: "Politics",
              value: "politics",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-cyan">Pillar 3 — Politics</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">The intelligence-driven discipline of electoral strategy, voter engagement, and political campaign architecture. Ground-level intelligence gathering, voter micro-segmentation, electoral timing optimization, coalition building, campaign execution — operating at the precision required for sovereign-scale outcomes.</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">Politics is the third pillar because the political environment shapes every other operational domain. Perception → Politics: intelligence gathered through Perception monitoring provides Politics with the information environment needed to target voters and time campaigns. Politics → Policy: ground-truth data informs Policy priorities.</p>
                </div>
              ),
            },
            {
              label: "Policing",
              value: "policing",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-emerald">Pillar 4 — Policing</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">The domain of intelligence coordination, law enforcement support, security operations, and threat neutralization. Intelligence agency coordination, counter-terrorism frameworks, threat assessment, forensic intelligence, defense infrastructure — the security operations that protect the operational architecture.</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">Policing is the fourth pillar because it provides the threat detection and security operations that allow the other pillars to function safely. Policy → Policing: governance priorities and legal frameworks direct Policing operations. Policing → Privacy: security intelligence identifies threats to communication infrastructure.</p>
                </div>
              ),
            },
            {
              label: "Policy",
              value: "policy",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-amber-gold">Pillar 5 — Policy</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">The framework for governance transformation, eGovernance implementation, and institutional delivery systems. Digital governance, institutional reform, scheme implementation tracking, citizen feedback integration — the policy architecture that institutionalizes the other four pillars.</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">Policy is the fifth pillar because it institutionalizes the operational outcomes. Politics → Policy: ground-truth data from voter engagement informs Policy priorities. Policy → Policing: governance priorities direct Policing operations. The closed loop continues.</p>
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">The framework in operational practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four examples of how the Penta P's Framework operates in sovereign-scale engagements. Each example illustrates the simultaneous activation of all five pillars.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "National Election Campaign", body: "All five pillars deploy simultaneously: Perception monitors the information environment. Privacy protects communications. Politics executes the campaign strategy. Policing identifies threats. Policy prepares the governance transition. Eighty-three point three percent success rate across eighteen engagements.", accent: "red" },
            { title: "Crisis Transformation", body: "Three pillars activate simultaneously — Perception for containment, Privacy for secure communications, Policing for threat neutralization. The crisis response engine operates at 384x to 1,416x the speed of traditional approaches.", accent: "purple" },
            { title: "Governance Reform", body: "Policy drives the reform agenda. Politics ensures stakeholder alignment. Perception manages the public narrative. Policing protects the institutional transition. Privacy safeguards the citizen data. All five pillars operate in coordinated unity.", accent: "cyan" },
            { title: "Corporate Brand Protection", body: "Perception monitors the brand across every channel. Privacy protects internal communications. Politics positions the entity against regulatory and political risk. Policing identifies threat actors. Policy ensures regulatory compliance. The framework produces brand protection at scale.", accent: "emerald" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What the Penta P's Framework is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions about the framework. The misconceptions are common because the term has been adopted in contexts where the discipline is not practiced.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is the Penta P's Framework the same as a consulting methodology?", a: "No. Consulting methodologies describe how advisors engage with client problems. The Penta P's Framework is an operational architecture that governs how integrated platforms, teams, and disciplines coordinate. The framework produces outcomes, not recommendations." },
            { q: "Are the five pillars independent capabilities?", a: "No. The five pillars share data, intelligence, and learnings across the entire stack. They are a closed-loop system where each pillar reinforces the others. No pillar functions effectively in isolation." },
            { q: "Can the framework be applied to small organizations?", a: "The framework is scalable. A ten-person organization and a sovereign-scale operation spanning eighteen countries can both apply the principles. The implementation mechanism varies. The framework itself does not." },
            { q: "Is the framework industry-specific?", a: "No. The framework applies across industries — government, defense, intelligence, political campaigns, corporate enterprises, royal families, and high-net-worth individuals. The operating context varies. The framework does not." },
            { q: "Does the framework replace traditional management disciplines?", a: "No. The framework integrates with traditional management disciplines — strategy, operations, finance, human resources, technology. The framework adds the sovereign-scale integration layer that allows the disciplines to operate in coordinated unity." },
            { q: "Is the framework the same as a technology stack?", a: "No. The framework is a closed-loop system of five operational pillars. The technology stack is the substrate that enables the framework. The technology supports the operations. The operations produce the outcomes." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Network className="size-10 text-cyan mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the closed-loop system.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The Penta P's Framework is not a consulting methodology. It is an operational architecture — a closed-loop system that governs how integrated platforms, sixty-plus services, and multidisciplinary teams coordinate.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/glossary/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                Glossary Hub
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "FAQ: Leadership", description: "Clinical leadership and the Penta P's framework.", href: "/faq/leadership/", icon: Crown, accent: "red" },
          { title: "Operational Excellence", description: "The engineered system that produces outcomes.", href: "/glossary/operational-excellence/", icon: Target, accent: "amber-gold" },
          { title: "About Dr. Jyoti Kush", description: "The full story of the operational architect.", href: "/about/", icon: Crown, accent: "purple" },
        ]}
      />
    </>
  );
}
